import { Component, EventEmitter, Inject, Input, OnInit, OnDestroy, Output } from '@angular/core';

import Env from '@ridango/core/env';
import { ApplicationFacade, ProductsFacade, PurchaseService } from '@ridango/components';
import { CardType, ProductArea, Ticket, ValueCode, ProductCode } from '@ridango/components/lib/purchase/purchase.model';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs/operators';
import { AppStartupService, BaseComponent } from '@ridango/core';
import { ModalController } from '@ionic/angular';
import { DeleteValueCodeModalComponent } from '../delete-value-code-modal/delete-value-code-modal.component';
import { AnalyticsService } from '@ridango/core/lib/services/analytics.service';
import { TrackedEventCategory } from '@ridango/core/lib/enums/analytics/tracked-event-categories.enum';
import { TrackedEventAction } from '@ridango/core/lib/enums/analytics/tracked-event-action.enum';
import { fixedValidityRangeOptionsFromAppStartup, resolveFixedValidityRange } from '@ridango/widgets';

@Component({
    selector: 'gl-shopping-cart-ticket',
    templateUrl: './shopping-cart-ticket.component.html',
    styleUrls: ['./shopping-cart-ticket.component.scss']
})
export class ShoppingCartTicketComponent extends BaseComponent implements OnInit, OnDestroy {
    @Input() ticket: Ticket;
    @Input() totalPrice: number;
    @Input() removeMode: boolean;
    @Input() valueCodes: number[];
    @Input() productAreas: ProductArea[];
    @Input() count = 1;

    @Output() ticketDelete: EventEmitter<number | string> = new EventEmitter<number | string>();
    @Output() valueCodeDelete: EventEmitter<string> = new EventEmitter<string>();
    public expanded = false;
    public assetsPath$ = this.applicationFacade.assetsPath$;
    public deleteTicketModalOpen = false;
    public currentLanguage = this.translate.currentLang;
    public cardType = CardType;
    public ProductCode = ProductCode;
    currency = this.appStartupService.getCurrency();
    private REMOVE_SELECTED_PRODUCT = 'Remove selected product';
    private REMOVE_FROM_CART = 'Remove from cart';
    private _isActivateNow: boolean | null = null;
    private productsRequested = false;
    private latestProducts: Array<{
        product_id?: number | string;
        product_type?: string;
        product_properties?: unknown[];
    }> = [];

    constructor(
        @Inject('ENV') public env: Env,
        private applicationFacade: ApplicationFacade,
        private translate: TranslateService,
        private modalCtrl: ModalController,
        private productsFacade: ProductsFacade,
        private purchaseService: PurchaseService,
        public appStartupService: AppStartupService,
        private analyticsService: AnalyticsService
    ) {
        super();
    }

    ngOnInit() {
        this.translate.onLangChange.pipe(takeUntil(this.componentDestroyed$)).subscribe((event) => {
            this.currentLanguage = event.lang;
        });

        this.productsFacade.products$.pipe(takeUntil(this.componentDestroyed$)).subscribe((products) => {
            if (!this.productsRequested && products.length === 0) {
                this.productsRequested = true;
                this.productsFacade.fetchPurchaseProducts(this.env.CHANNEL);
                return;
            }

            if (!products.length) {
                return;
            }

            this.latestProducts = products;
            this.enrichTicketWithProduct();
        });

        if (this.ticket?.starting) {
            const startingDate = new Date(this.ticket.starting);
            const now = new Date();
            // Add 5 second buffer - consider anything within 5 seconds as "activate now"
            // Otherwise it showed today's date but a few seconds later it would show Upon purchase
            this._isActivateNow = startingDate.getTime() <= now.getTime() + 5000;
        }
    }

    handleDeleteClicked() {
        this.analyticsService.eventTrack(TrackedEventAction.CLICKED, {
            category: TrackedEventCategory.PURCHASE_FLOW,
            name: `${this.REMOVE_SELECTED_PRODUCT} - ${this.REMOVE_FROM_CART}`
        });

        this.ticketDelete.emit(this.env.IS_NEW_AFC ? this.ticket.productId : this.ticket.id);
        this.deleteTicketModalOpen = false;
    }

    handleExpandClicked() {
        this.expanded = !this.expanded;
    }

    openDeleteTicketModal() {
        this.deleteTicketModalOpen = true;
    }

    closeDeleteTicketModal() {
        this.deleteTicketModalOpen = false;
    }

    getPriceString(price: number) {
        const formattedCents = price % 100 < 10 ? '0' + (price % 100) : '' + (price % 100);

        return `${Math.floor(price / 100)},${formattedCents}`;
    }

    get ticketValueCodes(): ValueCode[] {
        const valueCodes = (this.ticket as Ticket & { valueCodes?: ValueCode | ValueCode[] | null })?.valueCodes;

        if (Array.isArray(valueCodes)) {
            return valueCodes;
        }

        if (valueCodes && typeof valueCodes === 'object') {
            return [valueCodes];
        }

        return [];
    }

    hasFixedValidityRange(): boolean {
        return !!resolveFixedValidityRange(this.ticket as Ticket & { product?: unknown; product_object?: unknown }, {
            ...fixedValidityRangeOptionsFromAppStartup(this.appStartupService)
        });
    }

    fixedValidityRangeStart(): Date | undefined {
        return resolveFixedValidityRange(this.ticket as Ticket & { product?: unknown; product_object?: unknown }, {
            ...fixedValidityRangeOptionsFromAppStartup(this.appStartupService)
        })?.start;
    }

    fixedValidityRangeEnd(): Date | undefined {
        return resolveFixedValidityRange(this.ticket as Ticket & { product?: unknown; product_object?: unknown }, {
            ...fixedValidityRangeOptionsFromAppStartup(this.appStartupService)
        })?.end;
    }

    private enrichTicketWithProduct(): void {
        if (!this.ticket || !this.latestProducts.length) {
            return;
        }

        const existingProduct = (this.ticket as Ticket & { product?: { product_properties?: unknown[] } }).product;
        if (existingProduct?.product_properties?.length) {
            return;
        }

        const product = this.latestProducts.find((candidate) => candidate.product_id == this.ticket.productId);

        if (product) {
            (this.ticket as Ticket & { product?: unknown; product_type?: string }).product = product;

            if (!(this.ticket as Ticket & { product_type?: string }).product_type && product.product_type) {
                (this.ticket as Ticket & { product_type?: string }).product_type = product.product_type;
            }
        }
    }

    currentProductArea() {
        return this.productAreas?.find(
            (pa) => pa.startZoneId === this.ticket.startZoneId || pa.endZoneId === this.ticket.endZoneId
        );
    }

    async openDeleteValueCodeModal(code: ValueCode) {
        const modal = await this.modalCtrl.create({
            component: DeleteValueCodeModalComponent,
            cssClass: 'delete-value-code-modal',
            componentProps: {
                code
            }
        });

        await modal.present();

        modal.onDidDismiss().then(async ({ data }) => {
            if (data) {
                this.valueCodeDelete.emit(code.serialNumber);
            }
        });
    }

    isActivateNow(): boolean {
        return this._isActivateNow ?? false;
    }
}
